import React, { FC, useEffect, useMemo, useState } from 'react'
import Modal from '../common/Modal'
import { nowDate } from '../../../utils/date'
import iconv = require('iconv-lite')
import Encoding from 'encoding-japanese'
import { useAlert } from 'react-alert'

// eslint-disable-next-line @typescript-eslint/no-var-requires
const FileDownload = require('js-file-download')

interface UpdateCost {
  member_id: number
  hourly_pay: number | null
}

interface ResultData {
  id: number
  name: string
  email: string
  code: string | null
  before_hourly_pay: number | null
  after_hourly_pay: number | null
}

interface Props {
  isDisplay: boolean
  memberList: {
    id: number
    name: string
    email: string
    code: string | null
    profile_image: string | null
    hourly_pay: number | null
  }[]
  closeCallback: VoidFunction
  changeCallback: (data: UpdateCost[]) => void
}

const ImportMemberCostModal: FC<Props> = (props) => {
  const {
    isDisplay,
    memberList,
    closeCallback,
    changeCallback,
  } = props

  const alert = useAlert()

  const [fileData, setFileData] = useState("")
  const [step, setStep] = useState<number>(1)
  const [errorMessage, setErrorMessage] = useState<string>("")

  const formatCost = (value: string) => (
    Number(value.replace(/[０-９]/g, (s) => String.fromCharCode(s.charCodeAt(0) - 0xFEE0)).replace(/[^0-9]/g, ''))
  )

  const buildData = useMemo(() => {
    if (!fileData) return []
    const parseRow = fileData.split('\n')
    if (parseRow[0] !== "社員コード(変更不可),氏名(変更不可),メールアドレス(変更不可),1時間あたりのコスト") {
      setErrorMessage("指定外のCSVファイルを検知しました")
      return []
    }
    const parseList = parseRow.filter((_v, i) => i !== 0).map((row) => row.split('","'))
      .map((v) => ({ code: v[0].replace(/"/g, ''), name: v[1].replace(/"/g, ''), email: v[2].replace(/"/g, ''), after_hourly_pay: formatCost(v[3]) }))
    const resultData: ResultData[] = []
    parseList.forEach((v) => {
      const member = memberList.find((w) => w.email === v.email && w.hourly_pay !== v.after_hourly_pay)
      if (!member) return
      resultData.push({
        ...v,
        id: member.id,
        name: member.name,
        code: member.code,
        before_hourly_pay: member.hourly_pay,
      })
    })
    if (!resultData.length) {
      setErrorMessage("変更されたデータがありませんでした")
      return []
    }
    return resultData
  }, [fileData])

  const downloadCsv = (code: string) => {
    const resultRows = memberList.map((member) => (
      `"${member.code || ""}","${member.name}","${member.email}","${member.hourly_pay || ""}"`
    ))
    resultRows.unshift(`社員コード(変更不可),氏名(変更不可),メールアドレス(変更不可),1時間あたりのコスト`)
    const filename = `メンバーコスト一括登録_${nowDate("YYYYMMDD")}`.slice(0, 150)
    if (code === 'Shift-JIS') {
      const encodeData = iconv.encode(resultRows.join('\n'), "windows-31j")
      FileDownload(new Blob([encodeData]), `${filename}.csv`)
    } else {
      FileDownload(resultRows.join('\n'), `${filename}.csv`)
    }
  }

  const uploadCsv = (uploadFile: FileList | null) => {
    setErrorMessage("")
    if (!uploadFile || uploadFile[0].type !== "text/csv") {
      setErrorMessage("不正なファイルを検知しました")
      return
    }
    const reader = new FileReader()
    reader.onload = (e) => {
      if (e.target === null || e.target.result === null) return
      const buf = new Uint8Array(e.target.result as ArrayBuffer)
      const codes = Encoding.detect(new Uint8Array(buf))
      if (codes === 'UNICODE' || codes === 'SJIS') {
        setFileData(iconv.decode(Buffer.from(buf), "windows-31j"))
        return
      }

      setFileData(new TextDecoder("utf-8").decode(buf))
    }
    reader.onerror = () => alert.error("不正なファイルが読み込まれました")
    reader.readAsArrayBuffer(uploadFile[0])
  }

  const prev = () => {
    setStep(step - 1)
    setFileData("")
    setErrorMessage("")
  }

  const submitCost = () => {
    if (!buildData.length) return
    const tmpData = buildData.map((v) => ({ member_id: v.id, hourly_pay: v.after_hourly_pay }))
    changeCallback(tmpData)
    closeCallback()
  }

  const buildButtonArea = () => {
    let prevButton = (
      <button
        type="button"
        className="button cancel"
        onClick={prev}
      >
        戻る
      </button>
    )
    let nextButton = (
      <button
        type="button"
        className="button"
        onClick={() => setStep(step + 1)}
        disabled={step === 2 && !buildData.length}
      >
        次へ
      </button>
    )
    if (step === 1) {
      prevButton = <></>
    }
    if (step === 3) {
      nextButton = (
        <button
          type="button"
          className="button"
          onClick={submitCost}
        >
          変更する
        </button>
      )
    }
    return (
      <div className="button_area">
        {prevButton}
        {nextButton}
      </div>
    )
  }

  useEffect(() => {
    setStep(1)
    setFileData("")
    setErrorMessage("")
  }, [isDisplay])

  return (
    <Modal
      show={isDisplay}
      wrapperClass="import_member_cost"
      closeCallback={closeCallback}
      closeable
    >
      <>
        <div className="modal_title">
          メンバーコストを一括登録する
        </div>
        {
          step === 1 && (
            <>
              <div className="modal_detail">
                <div className="step_title">
                  STEP.1 メンバーコスト一括登録用CSVファイルのダウンロード
                </div>
                <div className="step_detail">
                  <div className="detail_text_area">
                    <div>
                      メンバーコスト一括登録用CSVファイルのダウンロードをおこない、「1時間あたりのコスト」列を編集してください。
                    </div>
                    <div className="red">
                      ※「1時間あたりのコスト」列以外の編集は変更に反映されません。ご注意ください。
                    </div>
                    <div className="red">
                      ※ExcelでCSVを開く方はShift-JISを選択して下さい。
                    </div>
                  </div>
                  <div className="detail_button_area">
                    <button
                      type="button"
                      className="download"
                      onClick={() => downloadCsv('Shift-JIS')}
                    >
                      メンバーコスト一括登録用(Shift-JIS)
                    </button>
                    <button
                      type="button"
                      className="download"
                      onClick={() => downloadCsv('UTF-8')}
                    >
                      メンバーコスト一括登録用(UTF-8)
                    </button>
                  </div>
                </div>
              </div>
            </>
          )
        }
        {
          step === 2 && (
            <>
              <div className="modal_detail">
                <div className="step_title">
                  STEP.2 メンバーコスト一括登録用CSVファイルのアップロード
                </div>
                <div className="step_detail">
                  <div className="detail_text_area">
                    <div>
                      編集したメンバーコスト一括登録用CSVファイルをアップロードしてください。
                    </div>
                  </div>
                  <div className="upload_area">
                    <label htmlFor="import_csv">
                      メンバーコスト一括登録用CSVファイルをアップロードしてください。
                      <input
                        type="file"
                        id="import_csv"
                        name="import_csv"
                        accept="text/csv"
                        onChange={(e) => uploadCsv(e.currentTarget.files)}
                      />
                    </label>
                  </div>
                  {
                    errorMessage ? (
                      <div className="error_message">
                        {errorMessage}
                      </div>
                    ) : (
                      <></>
                    )
                  }
                </div>
              </div>
            </>
          )
        }
        {
          step === 3 && (
            <>
              <div className="modal_detail">
                <div className="step_title">
                  STEP.3 変更する情報の確認
                </div>
                <div className="step_detail">
                  <div className="detail_text_area">
                    <div>
                      以下の内容で変更をおこないます。
                    </div>
                  </div>
                  <div className="detail_result_area">
                    {
                      buildData.map((data) => (
                        <div
                          key={`data_list_${data.email}`}
                          className="result_row"
                        >
                          <div>
                            {data.name}
                            {data.code && `(${data.code})`}
                          </div>
                          <div className="cost_area">
                            <div className="display_cost">
                              {data.before_hourly_pay ? `¥${data.before_hourly_pay.toLocaleString()}` : "設定なし"}
                            </div>
                            <div>
                              →
                            </div>
                            <div className="display_cost">
                              {data.after_hourly_pay ? `¥${data.after_hourly_pay.toLocaleString()}` : "設定なし"}
                            </div>
                          </div>
                        </div>
                      ))
                    }
                  </div>
                </div>
              </div>
            </>
          )
        }
        {buildButtonArea()}
      </>
    </Modal>
  )
}

export default ImportMemberCostModal
